import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import { useEffect, useRef, useState } from 'react';
import { MdCheck, MdClose, MdSearch, MdWarning } from 'react-icons/md';
import { createStockMovement } from 'interfaces/stockMovement';

interface InventoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    stock: any[];
    tags: any[];
    userCategories: any[];
    userSubCategories: any[];
    allSuppliers: any[];
    onDone: () => void;
}

const InventoryModal = ({
    isOpen,
    onClose,
    stock,
    tags,
    userCategories,
    userSubCategories,
    allSuppliers,
    onDone,
}: InventoryModalProps) => {
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [subCategoryFilter, setSubCategoryFilter] = useState('all');
    const [tagFilter, setTagFilter] = useState('all');
    const [supplierFilter, setSupplierFilter] = useState('all');
    const [alertFilter, setAlertFilter] = useState<'all' | 'alert' | 'ok'>('all');
    const [quantities, setQuantities] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [savedItems, setSavedItems] = useState<Set<string>>(new Set());
    const [errorItems, setErrorItems] = useState<Set<string>>(new Set());
    const searchRef = useRef<HTMLInputElement>(null);
    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    // Subcategories available for selected category
    const availableSubCategories = categoryFilter === 'all'
        ? userSubCategories
        : userSubCategories.filter((sc: any) => sc.categoryId === categoryFilter);

    // Reset subcategory when category changes
    useEffect(() => {
        setSubCategoryFilter('all');
    }, [categoryFilter]);

    // Initialize quantities from stock on open
    useEffect(() => {
        if (isOpen && stock) {
            const initial: Record<string, string> = {};
            stock.forEach((item: any) => {
                initial[item._id] = String(Number(item.quantity ?? 0));
            });
            setQuantities(initial);
            setSavedItems(new Set());
            setErrorItems(new Set());
            setSearch('');
            setTypeFilter('all');
            setCategoryFilter('all');
            setSubCategoryFilter('all');
            setTagFilter('all');
            setSupplierFilter('all');
            setAlertFilter('all');
        }
    }, [isOpen, stock]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => searchRef.current?.focus(), 150);
        }
    }, [isOpen]);

    const resolveProductTag = (p: any) => {
        const raw = p?.productTag ?? p?.tagId ?? null;
        if (!raw) return null;
        if (typeof raw === 'object') return raw;
        return tags.find((t: any) => t._id === raw) || null;
    };

    const getProductSupplierName = (product: any): string => {
        if (!product) return '';
        if (product.supplier?.displayName) return product.supplier.displayName;
        if (product.supplier?.company) return product.supplier.company;
        if (product.supplier?.name) return product.supplier.name;
        if (product.supplierName) return product.supplierName;
        if (Array.isArray(product.suppliers) && product.suppliers.length > 0) {
            const first = product.suppliers[0];
            if (typeof first === 'object') {
                return first?.displayName || first?.company || first?.name || '';
            }
            if (typeof first === 'string' && allSuppliers.length > 0) {
                const s = allSuppliers.find((s: any) => s._id === first);
                return s ? (s.displayName || s.company || s.name || '') : '';
            }
        }
        return '';
    };

    const getUniqueSuppliers = () => {
        if (!stock) return [];
        const set = new Set<string>();
        stock.forEach((item: any) => {
            const name = getProductSupplierName(item);
            if (name && name.trim()) set.add(name.trim());
        });
        return Array.from(set).sort();
    };

    const filteredStock = (stock || []).filter((item: any) => {
        if (search) {
            const s = search.toLowerCase();
            if (
                !(item.name || '').toLowerCase().includes(s) &&
                !(item.ref || '').toLowerCase().includes(s) &&
                !(item.ean || '').toLowerCase().includes(s)
            )
                return false;
        }
        if (typeFilter !== 'all' && item.productType !== typeFilter) return false;

        // product.category stores the user category ID (e.g. "zone-a-legumes")
        if (categoryFilter !== 'all') {
            if (item.category !== categoryFilter) return false;
        }

        // product.productSubCategory stores the subcategory NAME (e.g. "Légumes")
        if (subCategoryFilter !== 'all') {
            if (item.productSubCategory !== subCategoryFilter) return false;
        }

        if (tagFilter !== 'all') {
            const tag = resolveProductTag(item);
            if ((tag?._id ?? null) !== tagFilter) return false;
        }
        if (supplierFilter !== 'all') {
            if (supplierFilter === 'none') {
                const name = getProductSupplierName(item);
                if (name && name.trim()) return false;
            } else {
                if (getProductSupplierName(item).trim() !== supplierFilter) return false;
            }
        }
        if (alertFilter !== 'all') {
            const quantity = Number(item.quantity ?? 0);
            const threshold = Number(item.alert);
            const hasThreshold = !Number.isNaN(threshold);
            if (!hasThreshold) return false;
            if (alertFilter === 'alert') return quantity <= threshold;
            if (alertFilter === 'ok') return quantity > threshold;
        }
        return true;
    });

    // Sort: category position → subcategory position → name
    const getCategoryPosition = (item: any) => {
        const cat = userCategories.find((c: any) => c.id === item.category);
        return cat?.position ?? 99999;
    };
    const getSubCategoryPosition = (item: any) => {
        const sc = userSubCategories.find((s: any) => s.name === item.productSubCategory);
        return sc?.position ?? 99999;
    };

    const sortedStock = [...filteredStock].sort((a: any, b: any) => {
        const catDiff = getCategoryPosition(a) - getCategoryPosition(b);
        if (catDiff !== 0) return catDiff;
        const subDiff = getSubCategoryPosition(a) - getSubCategoryPosition(b);
        if (subDiff !== 0) return subDiff;
        return (a.name || '').localeCompare(b.name || '');
    });

    // Build rows with category/subcategory section headers
    type TableRow =
        | { type: 'category-header'; label: string; key: string }
        | { type: 'subcategory-header'; label: string; key: string }
        | { type: 'product'; item: any };

    const buildGroupedRows = (): TableRow[] => {
        const rows: TableRow[] = [];
        let lastCatId: string | null = null;
        let lastSubCatId: string | null = null;

        sortedStock.forEach((item: any) => {
            const catId = item.category || '__none__';
            const subCatId = item.productSubCategory || '__none__';

            if (catId !== lastCatId) {
                const cat = userCategories.find((c: any) => c.id === item.category);
                if (cat) {
                    rows.push({
                        type: 'category-header',
                        label: `${cat.icon && cat.icon !== 'none' ? cat.icon + ' ' : ''}${cat.name}`,
                        key: `cat-${catId}`,
                    });
                } else if (catId === '__none__' && lastCatId !== null) {
                    rows.push({ type: 'category-header', label: 'Sans catégorie', key: 'cat-none' });
                }
                lastCatId = catId;
                lastSubCatId = null;
            }

            if (subCatId !== lastSubCatId) {
                // productSubCategory stores the subcategory NAME
                const sc = userSubCategories.find((s: any) => s.name === item.productSubCategory);
                if (sc) {
                    rows.push({ type: 'subcategory-header', label: sc.name, key: `subcat-${subCatId}-in-${lastCatId}` });
                } else if (subCatId !== '__none__') {
                    // Display the raw name if not found in userSubCategories
                    rows.push({ type: 'subcategory-header', label: item.productSubCategory, key: `subcat-${subCatId}-in-${lastCatId}` });
                }
                lastSubCatId = subCatId;
            }

            rows.push({ type: 'product', item });
        });

        return rows;
    };

    const groupedRows = buildGroupedRows();

    const hasChanges = Object.keys(quantities).some((id) => {
        const original = stock?.find((s: any) => s._id === id);
        if (!original) return false;
        return Number(quantities[id]) !== Number(original.quantity ?? 0);
    });

    const changedCount = Object.keys(quantities).filter((id) => {
        const original = stock?.find((s: any) => s._id === id);
        if (!original) return false;
        return Number(quantities[id]) !== Number(original.quantity ?? 0);
    }).length;

    const saveAll = async () => {
        setSaving(true);
        const newSaved = new Set(savedItems);
        const newErrors = new Set(errorItems);

        const changedItems = Object.keys(quantities).filter((id) => {
            const original = stock?.find((s: any) => s._id === id);
            if (!original) return false;
            return Number(quantities[id]) !== Number(original.quantity ?? 0);
        });

        await Promise.all(
            changedItems.map(async (id) => {
                const original = stock?.find((s: any) => s._id === id);
                if (!original) return;
                const currentQty = Number(original.quantity ?? 0);
                const newQty = Number(quantities[id]);
                const delta = newQty - currentQty;

                try {
                    if (delta !== 0) {
                        const res = await createStockMovement({
                            objectId: id,
                            quantity: delta,
                            reason: 'Inventaire',
                            area: 'zone-a',
                        });
                        if (res.error) {
                            newErrors.add(id);
                        } else {
                            newSaved.add(id);
                            newErrors.delete(id);
                        }
                    }
                } catch {
                    newErrors.add(id);
                }
            }),
        );

        setSavedItems(newSaved);
        setErrorItems(newErrors);
        setSaving(false);

        if (newErrors.size === 0) {
            onDone();
            onClose();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, currentId: string) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        // Build ordered list of visible product ids from groupedRows
        const productIds = groupedRows
            .filter((r) => r.type === 'product')
            .map((r: any) => r.item._id);
        const idx = productIds.indexOf(currentId);
        if (idx === -1) return;
        const nextId = e.key === 'ArrowDown' ? productIds[idx + 1] : productIds[idx - 1];
        if (nextId && inputRefs.current[nextId]) {
            inputRefs.current[nextId]?.focus();
            inputRefs.current[nextId]?.select();
        }
    };

    const handleQuantityChange = (id: string, value: string) => {
        setQuantities((prev) => ({ ...prev, [id]: value }));
        // Clear saved/error state when editing again
        setSavedItems((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        setErrorItems((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const getRowStyle = (id: string) => {
        if (savedItems.has(id)) return 'bg-green-50';
        if (errorItems.has(id)) return 'bg-red-50';
        const original = stock?.find((s: any) => s._id === id);
        if (!original) return '';
        if (Number(quantities[id]) !== Number(original.quantity ?? 0))
            return 'bg-blue-50';
        return '';
    };

    const getQuantityDelta = (id: string) => {
        const original = stock?.find((s: any) => s._id === id);
        if (!original) return null;
        const delta = Number(quantities[id]) - Number(original.quantity ?? 0);
        if (delta === 0) return null;
        return delta;
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl">
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!m-auto !w-[95vw] !max-w-[1200px] md:top-[4vh]">
                <ModalBody className="p-0">
                    <div className="flex flex-col bg-white rounded-2xl overflow-hidden max-h-[92vh]">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    Gestion des stocks
                                </h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    Modifiez les quantités en stock pour chaque produit
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                            >
                                <MdClose size={20} className="text-gray-500" />
                            </button>
                        </div>

                        {/* Filters */}
                        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-2 items-center">
                            {/* Search */}
                            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 min-w-[200px] flex-1 max-w-[300px]">
                                <MdSearch size={16} className="text-gray-400 shrink-0" />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    placeholder="Rechercher..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="text-sm outline-none bg-transparent w-full text-gray-700 placeholder-gray-400"
                                />
                                {search && (
                                    <button onClick={() => setSearch('')}>
                                        <MdClose size={14} className="text-gray-400" />
                                    </button>
                                )}
                            </div>

                            {/* Type filter */}
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 outline-none"
                            >
                                <option value="all">Tous les types</option>
                                <option value="brut">Produit brut</option>
                                <option value="transformed">Produit transformé</option>
                                <option value="assembled">Produit assemblé</option>
                            </select>

                            {/* Category filter */}
                            {userCategories.length > 0 && (
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 outline-none"
                                >
                                    <option value="all">Toutes catégories</option>
                                    {[...userCategories]
                                        .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
                                        .map((cat: any) => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.icon && cat.icon !== 'none' ? `${cat.icon} ` : ''}{cat.name}
                                            </option>
                                        ))}
                                </select>
                            )}

                            {/* Subcategory filter — visible when a category is selected and has subcategories */}
                            {categoryFilter !== 'all' && availableSubCategories.length > 0 && (
                                <select
                                    value={subCategoryFilter}
                                    onChange={(e) => setSubCategoryFilter(e.target.value)}
                                    className="text-sm border border-[#0260CB]/40 rounded-lg px-2 py-1.5 bg-white text-[#0260CB] outline-none"
                                >
                                    <option value="all">Toutes sous-catégories</option>
                                    {[...availableSubCategories]
                                        .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
                                        .map((sc: any) => (
                                            <option key={sc.id} value={sc.name}>
                                                {sc.name}
                                            </option>
                                        ))}
                                </select>
                            )}

                            {/* Tag filter */}
                            {tags.length > 0 && (
                                <select
                                    value={tagFilter}
                                    onChange={(e) => setTagFilter(e.target.value)}
                                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 outline-none"
                                >
                                    <option value="all">Tous les tags</option>
                                    {tags.map((t: any) => (
                                        <option key={t._id} value={t._id}>
                                            {t.name}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {/* Supplier filter */}
                            {getUniqueSuppliers().length > 0 && (
                                <select
                                    value={supplierFilter}
                                    onChange={(e) => setSupplierFilter(e.target.value)}
                                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 outline-none"
                                >
                                    <option value="all">Tous fournisseurs</option>
                                    <option value="none">Sans fournisseur</option>
                                    {getUniqueSuppliers().map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            )}

                            {/* Alert filter */}
                            <select
                                value={alertFilter}
                                onChange={(e) => setAlertFilter(e.target.value as any)}
                                className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 outline-none"
                            >
                                <option value="all">Toutes alertes</option>
                                <option value="alert">En alerte</option>
                                <option value="ok">En stock</option>
                            </select>

                            <span className="ml-auto text-xs text-gray-400 whitespace-nowrap">
                                {filteredStock.length} produit{filteredStock.length !== 1 ? 's' : ''}
                            </span>
                        </div>

                        {/* Table */}
                        <div className="overflow-y-auto flex-1">
                            <table className="w-full text-sm text-left">
                                <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-gray-600">Produit</th>
                                        <th className="px-4 py-3 font-semibold text-gray-600 hidden sm:table-cell">Réf.</th>
                                        <th className="px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">Type</th>
                                        <th className="px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">Fournisseur</th>
                                        <th className="px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">Tag</th>
                                        <th className="px-4 py-3 font-semibold text-gray-600">Unité</th>
                                        <th className="px-4 py-3 font-semibold text-gray-600 text-center w-36">Quantité en stock</th>
                                        <th className="px-4 py-3 font-semibold text-gray-600 text-center w-10"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedRows.map((row) => {
                                        if (row.type === 'category-header') {
                                            return (
                                                <tr key={row.key}>
                                                    <td colSpan={8} className="px-4 py-2 font-bold text-[#0260CB] text-sm bg-[#0260CB]/10 border-t-2 border-[#0260CB]/20">
                                                        {row.label}
                                                    </td>
                                                </tr>
                                            );
                                        }
                                        if (row.type === 'subcategory-header') {
                                            return (
                                                <tr key={row.key}>
                                                    <td colSpan={8} className="px-6 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-t border-gray-100">
                                                        — {row.label}
                                                    </td>
                                                </tr>
                                            );
                                        }
                                        const { item } = row;
                                        const delta = getQuantityDelta(item._id);
                                        const isAlert =
                                            !isNaN(Number(item.alert)) &&
                                            Number(item.quantity ?? 0) <= Number(item.alert);
                                        const tag = resolveProductTag(item);
                                        return (
                                            <tr
                                                key={item._id}
                                                className={`transition-colors border-b border-gray-100 ${getRowStyle(item._id)} hover:bg-gray-50`}
                                            >
                                                {/* Name */}
                                                <td className="px-4 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        {isAlert && (
                                                            <MdWarning
                                                                size={14}
                                                                className="text-orange-400 shrink-0"
                                                                title="En alerte de stock"
                                                            />
                                                        )}
                                                        <span className="font-medium text-gray-900 truncate max-w-[200px]">
                                                            {item.name}
                                                        </span>
                                                    </div>
                                                </td>
                                                {/* Ref */}
                                                <td className="px-4 py-2.5 text-gray-500 hidden sm:table-cell">
                                                    {item.ref || '-'}
                                                </td>
                                                {/* Type */}
                                                <td className="px-4 py-2.5 text-gray-500 hidden md:table-cell">
                                                    {item.productType === 'brut'
                                                        ? 'Brut'
                                                        : item.productType === 'transformed'
                                                        ? 'Transformé'
                                                        : item.productType === 'assembled'
                                                        ? 'Assemblé'
                                                        : '-'}
                                                </td>
                                                {/* Supplier */}
                                                <td className="px-4 py-2.5 text-gray-500 hidden lg:table-cell truncate max-w-[120px]">
                                                    {getProductSupplierName(item) || '-'}
                                                </td>
                                                {/* Tag */}
                                                <td className="px-4 py-2.5 hidden lg:table-cell">
                                                    {tag ? (
                                                        <div className="flex items-center gap-1.5">
                                                            <span
                                                                className="inline-block w-3 h-3 rounded-full border border-white shadow-sm shrink-0"
                                                                style={{ backgroundColor: tag.color }}
                                                            />
                                                            <span className="text-xs text-gray-600 truncate max-w-[80px]">
                                                                {tag.name}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </td>
                                                {/* Unity */}
                                                <td className="px-4 py-2.5 text-gray-500">
                                                    {item.unity || '-'}
                                                </td>
                                                {/* Quantity input */}
                                                <td className="px-4 py-2.5 w-36">
                                                    <div className="flex flex-col items-center gap-0.5">
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            value={quantities[item._id] ?? ''}
                                                            ref={(el) => { inputRefs.current[item._id] = el; }}
                                                            onChange={(e) =>
                                                                handleQuantityChange(item._id, e.target.value)
                                                            }
                                                            onKeyDown={(e) => handleKeyDown(e, item._id)}
                                                            className={`w-full text-center border rounded-lg px-2 py-1.5 text-sm font-medium outline-none focus:ring-2 transition-all ${
                                                                delta !== null
                                                                    ? 'border-[#0260CB] focus:ring-[#0260CB]/20 text-[#0260CB]'
                                                                    : 'border-gray-200 focus:ring-gray-200 text-gray-800'
                                                            }`}
                                                        />
                                                        {delta !== null && (
                                                            <span
                                                                className={`text-xs font-semibold ${
                                                                    delta > 0
                                                                        ? 'text-green-600'
                                                                        : 'text-red-500'
                                                                }`}
                                                            >
                                                                {delta > 0 ? '+' : ''}
                                                                {delta}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                {/* Status icon */}
                                                <td className="px-2 py-2.5 text-center w-10">
                                                    {savedItems.has(item._id) && (
                                                        <MdCheck
                                                            size={16}
                                                            className="text-green-500 mx-auto"
                                                        />
                                                    )}
                                                    {errorItems.has(item._id) && (
                                                        <MdClose
                                                            size={16}
                                                            className="text-red-500 mx-auto"
                                                            title="Erreur lors de la sauvegarde"
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {filteredStock.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={8}
                                                className="px-4 py-10 text-center text-gray-400"
                                            >
                                                Aucun produit trouvé
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                            <div className="text-sm text-gray-500">
                                {hasChanges ? (
                                    <span className="font-medium text-[#0260CB]">
                                        {changedCount} produit{changedCount !== 1 ? 's' : ''} modifié
                                        {changedCount !== 1 ? 's' : ''}
                                    </span>
                                ) : (
                                    <span>Aucune modification</span>
                                )}
                                {errorItems.size > 0 && (
                                    <span className="ml-3 text-red-500">
                                        {errorItems.size} erreur{errorItems.size > 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={saveAll}
                                    disabled={!hasChanges || saving}
                                    className={`px-5 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                                        hasChanges && !saving
                                            ? 'bg-[#0260CB] hover:bg-[#01499b] active:bg-[#024086]'
                                            : 'bg-gray-300 cursor-not-allowed'
                                    }`}
                                >
                                    {saving ? 'Sauvegarde...' : 'Enregistrer l\'inventaire'}
                                </button>
                            </div>
                        </div>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default InventoryModal;
